import { supabase } from "@/integrations/supabase/client";
import ExifReader from 'exifreader';
import { generateImageEmbedding } from './clipEmbeddings';
import { debug } from './debug';
import { convertHeicToJpeg, convertSvgToPng } from './imageConversion';
import { toast } from '@/hooks/use-toast';

/**
 * Retry a function with exponential backoff
 */
async function retryWithBackoff<T>(
  fn: () => Promise<T>,
  maxRetries = 5,
  baseDelay = 1000,
  context = ''
): Promise<T> {
  let lastError: any;
  
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await fn();
    } catch (error: any) {
      lastError = error;
      
      // Check if it's a rate limit error
      const isRateLimit = error?.message?.includes('rate limit') || 
                         error?.message?.includes('Rate limit') ||
                         error?.error?.includes('rate limit') ||
                         error?.error?.includes('Rate limit') ||
                         error?.status === 429;
      
      if (!isRateLimit || attempt === maxRetries) {
        throw error;
      }
      
      // Exponential backoff: 1s, 2s, 4s, 8s, 16s
      const delay = baseDelay * Math.pow(2, attempt);
      debug.log(`${context} Rate limit hit, retrying in ${delay}ms (attempt ${attempt + 1}/${maxRetries})`);
      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }
  
  throw lastError;
}

export interface UploadPhotoParams {
  file: File;
  organizationId: string;
  description?: string;
  tags?: string[];
}

export interface UploadPhotoResult {
  success: boolean;
  photoId?: string;
  error?: string;
}

interface ExifData {
  camera_make?: string;
  camera_model?: string;
  aperture?: string;
  iso?: number;
  shutter_speed?: string;
  focal_length?: string;
  date_taken?: string;
  gps_latitude?: number;
  gps_longitude?: number;
  flash_used?: boolean;
}

const extractExifData = async (file: File): Promise<ExifData> => {
  try {
    const arrayBuffer = await file.arrayBuffer();
    const tags = ExifReader.load(arrayBuffer);
    
    debug.log('EXIF Tags found:', Object.keys(tags));
    
    const exifData: ExifData = {};

    // Camera info
    if (tags.Make?.description) {
      exifData.camera_make = tags.Make.description;
      debug.log('Camera Make:', exifData.camera_make);
    }
    if (tags.Model?.description) {
      exifData.camera_model = tags.Model.description;
      debug.log('Camera Model:', exifData.camera_model);
    }

    // Exposure settings
    if (tags.FNumber?.description) {
      exifData.aperture = tags.FNumber.description;
      debug.log('Aperture:', exifData.aperture);
    }
    if (tags.ISOSpeedRatings?.description) {
      exifData.iso = parseInt(tags.ISOSpeedRatings.description);
      debug.log('ISO:', exifData.iso);
    } else if (tags.ISO?.description) {
      exifData.iso = parseInt(tags.ISO.description);
      debug.log('ISO (alt):', exifData.iso);
    }
    if (tags.ExposureTime?.description) {
      exifData.shutter_speed = tags.ExposureTime.description;
      debug.log('Shutter Speed:', exifData.shutter_speed);
    }
    if (tags.FocalLength?.description) {
      exifData.focal_length = tags.FocalLength.description;
      debug.log('Focal Length:', exifData.focal_length);
    }

    // Date taken - with fallbacks for PNG/XMP
    if (tags.DateTimeOriginal?.description || tags.DateTime?.description) {
      const dateStr = tags.DateTimeOriginal?.description || tags.DateTime?.description;
      const isoDate = dateStr?.replace(/^(\d{4}):(\d{2}):(\d{2})/, '$1-$2-$3');
      exifData.date_taken = isoDate;
      debug.log('Date Taken (EXIF):', exifData.date_taken);
    } else if (tags['Create Date']?.description) {
      // XMP CreateDate
      const dateStr = tags['Create Date'].description;
      const isoDate = dateStr?.replace(/^(\d{4}):(\d{2}):(\d{2})/, '$1-$2-$3');
      exifData.date_taken = isoDate;
      debug.log('Date Taken (XMP CreateDate):', exifData.date_taken);
    } else if (tags.DateCreated?.description) {
      const dateStr = tags.DateCreated.description;
      const isoDate = dateStr?.replace(/^(\d{4}):(\d{2}):(\d{2})/, '$1-$2-$3');
      exifData.date_taken = isoDate;
      debug.log('Date Taken (DateCreated):', exifData.date_taken);
    } else if (tags.tIME?.description) {
      // PNG tIME chunk
      exifData.date_taken = tags.tIME.description;
      debug.log('Date Taken (PNG tIME):', exifData.date_taken);
    } else {
      // Fallback: use file modification date
      const fileDate = new Date(file.lastModified).toISOString();
      exifData.date_taken = fileDate;
      debug.log('Date Taken (file lastModified fallback):', exifData.date_taken);
    }

    // GPS coordinates
    if (tags.GPSLatitude && tags.GPSLongitude) {
      const lat = parseFloat(String(tags.GPSLatitude.description));
      const lng = parseFloat(String(tags.GPSLongitude.description));
      if (!isNaN(lat)) exifData.gps_latitude = lat;
      if (!isNaN(lng)) exifData.gps_longitude = lng;
      debug.log('GPS:', exifData.gps_latitude, exifData.gps_longitude);
    }

    // Flash
    if (tags.Flash?.description) {
      exifData.flash_used = String(tags.Flash.description).toLowerCase().includes('fired');
      debug.log('Flash:', exifData.flash_used);
    }

    debug.log('Extracted EXIF data:', exifData);
    return exifData;
  } catch (error) {
    console.warn('Failed to extract EXIF data:', error);
    return {};
  }
};

/**
 * Upload a photo to Supabase storage and create a database record
 * Photos are stored in organization-based folders: {organizationId}/{photoId}.{extension}
 */
export async function uploadPhoto({
  file,
  organizationId,
  description = "",
  tags = [],
}: UploadPhotoParams): Promise<UploadPhotoResult> {
  const startTime = Date.now();
  debug.log(`[${file.name}] Starting upload (${(file.size / 1024 / 1024).toFixed(2)}MB)`);

  try {
    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp', 'image/bmp', 'image/svg+xml', 'image/heic', 'image/heif'];
    if (!allowedTypes.includes(file.type.toLowerCase()) &&
        !file.name.match(/\.(jpe?g|png|gif|webp|bmp|svg|heic|heif)$/i)) {
      return {
        success: false,
        error: `Invalid file type: ${file.type}. Only image files are allowed.`
      };
    }

    // Validate file size (50MB limit)
    const maxSize = 50 * 1024 * 1024; // 50MB
    if (file.size > maxSize) {
      return {
        success: false,
        error: `File size ${(file.size / 1024 / 1024).toFixed(2)}MB exceeds maximum allowed size of 50MB`
      };
    }

    // Detect and convert HEIC files
    if (file.type === 'image/heic' || file.type === 'image/heif' || 
        file.name.toLowerCase().endsWith('.heic') || 
        file.name.toLowerCase().endsWith('.heif')) {
      
      toast({
        title: "Converting HEIC image",
        description: "Converting to JPEG format...",
      });
      debug.log(`[${file.name}] Converting HEIC to JPEG...`);
      
      try {
        file = await convertHeicToJpeg(file);
        debug.log(`[${file.name}] HEIC conversion complete`);
      } catch (error) {
        console.error(`[${file.name}] HEIC conversion failed:`, error);
        return { 
          success: false, 
          error: `HEIC conversion failed: ${error instanceof Error ? error.message : 'Unknown error'}` 
        };
      }
    }

    // Extract EXIF data before upload
    debug.log(`  [${file.name}] Extracting EXIF data...`);
    const exifData = await extractExifData(file);
    
    // Get current user
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      console.error(`  [${file.name}] User not authenticated`);
      return { success: false, error: "User not authenticated" };
    }

    // Generate unique photo ID
    const photoId = crypto.randomUUID();
    const fileExtension = file.name.split('.').pop() || 'jpg';
    const fileName = `${photoId}.${fileExtension}`;
    
    // Storage path: {organizationId}/{fileName}
    const storagePath = `${organizationId}/${fileName}`;

    debug.log(`  [${file.name}] Uploading to storage: ${storagePath}`);
    // Upload to storage
    const { error: uploadError } = await supabase.storage
      .from('photos')
      .upload(storagePath, file, {
        cacheControl: '3600',
        upsert: false,
      });

    if (uploadError) {
      console.error(`  [${file.name}] Storage upload error:`, uploadError);
      return { success: false, error: `Upload failed: ${uploadError.message}` };
    }


    debug.log(`  [${file.name}] Storage upload complete`);

    // Generate signed URL (bucket is now private)
    const { data: signedData, error: signedError } = await supabase.storage
      .from('photos')
      .createSignedUrl(storagePath, 31536000); // 1 year expiry for initial upload
    
    if (signedError) {
      console.error(`  [${file.name}] Failed to generate signed URL:`, signedError);
      return { success: false, error: `Failed to generate access URL: ${signedError.message}` };
    }
    
    const photoUrl = signedData.signedUrl;

    // Get image dimensions
    debug.log(`  [${file.name}] Getting image dimensions...`);
    const dimensions = await getImageDimensions(file);

    // Create database record with pending status, NO description, and EXIF data
    debug.log(`  [${file.name}] Creating database record...`);
    const { data: photoData, error: dbError } = await supabase
      .from('photos')
      .insert({
        id: photoId,
        organization_id: organizationId,
        uploaded_by: user.id,
        filename: file.name,
        url: photoUrl,
        file_type: file.type,
        file_size: file.size,
        width: dimensions.width,
        height: dimensions.height,
        description: null, // Explicitly set to null until AI processing
        processing_status: 'pending',
        ...exifData,
      })
      .select()
      .single();

    if (dbError) {
      console.error(`  [${file.name}] Database insert error:`, dbError);
      // Clean up storage on database error
      debug.log(`  [${file.name}] Cleaning up storage...`);
      await supabase.storage.from('photos').remove([storagePath]);
      return { success: false, error: `Database error: ${dbError.message}` };
    }

    debug.log(`  [${file.name}] Database record created with ID: ${photoId}`);

    // Add tags if provided
    if (tags.length > 0) {
      debug.log(`  [${file.name}] Adding ${tags.length} tags...`);
      const { error: tagsError } = await addPhotoTags(photoId, organizationId, tags);
      if (tagsError) {
        console.warn(`  [${file.name}] Failed to add tags:`, tagsError);
      }
    }

    // Generate CLIP embedding locally in background
    debug.log(`  [${file.name}] Starting local CLIP embedding generation...`);
    (async () => {
      try {
        // For SVG files, convert to PNG for CLIP embedding
        let embeddingUrl = photoUrl;
        if (file.type === 'image/svg+xml' || photoUrl.toLowerCase().endsWith('.svg')) {
          debug.log(`  [${file.name}] Converting SVG to PNG for embedding...`);
          try {
            embeddingUrl = await convertSvgToPng(photoUrl, 1024);
            debug.log(`  [${file.name}] SVG conversion complete`);
          } catch (error) {
            console.warn(`  [${file.name}] SVG conversion failed, using original:`, error);
          }
        }

        // Generate embedding locally using CLIP
        debug.log(`  [${file.name}] Generating CLIP embedding...`);
        const embedding = await generateImageEmbedding(embeddingUrl);
        
        // Update photo with embedding (but don't mark complete yet)
        const { error: updateError } = await supabase
          .from('photos')
          .update({ 
            embedding: JSON.stringify(embedding)
          })
          .eq('id', photoId);
        
        if (updateError) {
          console.error(`  [${file.name}] Failed to update embedding:`, updateError);
          throw updateError;
        }
        debug.log(`  [${file.name}] CLIP embedding generated and saved successfully`);

        // Extract colors with retry and small delay to avoid overwhelming (non-blocking)
        await new Promise(resolve => setTimeout(resolve, 500));
        debug.log(`  [${file.name}] Extracting colors...`);
        
        try {
          await retryWithBackoff(
            async () => {
              const { error: colorError } = await supabase.functions.invoke('extract-colors', {
                body: { photoId, imageUrl: photoUrl }
              });
              if (colorError) throw colorError;
            },
            5,
            1000,
            `[${file.name}]`
          );
          debug.log(`  [${file.name}] Colors extracted successfully`);
        } catch (colorError) {
          console.warn(`  [${file.name}] Color extraction failed (non-blocking):`, colorError);
        }

        // Generate AI description & tags (best-effort)
        await new Promise(resolve => setTimeout(resolve, 400));
        debug.log(`  [${file.name}] Generating AI description & tags...`);
        await retryWithBackoff(
          async () => {
            const { error: metaError } = await supabase.functions.invoke('generate-metadata', {
              body: { photoId, imageUrl: photoUrl }
            });
            if (metaError) throw metaError;
          },
          3,
          1000,
          `[${file.name}]`
        );
        debug.log(`  [${file.name}] AI metadata generated`);

        // Mark as complete after all processing is done
        await supabase
          .from('photos')
          .update({ processing_status: 'complete' })
          .eq('id', photoId);
        
        debug.log(`  [${file.name}] All processing complete`);
      } catch (error) {
        // Mark as failed if any step fails
        await supabase
          .from('photos')
          .update({ processing_status: 'failed', has_error: true })
          .eq('id', photoId);
        console.error(`  [${file.name}] Background processing error:`, error);
      }
    })();

    const duration = ((Date.now() - startTime) / 1000).toFixed(2);
    debug.log(`  [${file.name}] Upload completed in ${duration}s`);
    
    return { success: true, photoId };
  } catch (error) {
    const duration = ((Date.now() - startTime) / 1000).toFixed(2);
    console.error(`  [${file.name}] Upload error after ${duration}s:`, error);
    return { 
      success: false, 
      error: error instanceof Error ? error.message : 'Unknown error' 
    };
  }
}

/**
 * Get image dimensions from file
 */
function getImageDimensions(file: File): Promise<{ width: number; height: number }> {
  return new Promise((resolve) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve({ width: img.width, height: img.height });
    };
    
    img.onerror = () => {
      URL.revokeObjectURL(url);
      resolve({ width: 0, height: 0 });
    };
    
    img.src = url;
  });
}

/**
 * Add tags to a photo
 */
async function addPhotoTags(
  photoId: string,
  organizationId: string,
  tagNames: string[]
): Promise<{ error?: string }> {
  try {
    // Get or create tags
    const tagIds: string[] = [];
    
    for (const tagName of tagNames) {
      const { data: existingTag } = await supabase
        .from('tags')
        .select('id')
        .eq('organization_id', organizationId)
        .eq('name', tagName)
        .maybeSingle();

      if (existingTag) {
        tagIds.push(existingTag.id);
      } else {
        const { data: newTag, error } = await supabase
          .from('tags')
          .insert({
            organization_id: organizationId,
            name: tagName,
          })
          .select('id')
          .single();

        if (error) {
          console.error('Error creating tag:', error);
          continue;
        }
        
        if (newTag) {
          tagIds.push(newTag.id);
        }
      }
    }

    // Link tags to photo
    if (tagIds.length > 0) {
      const { error } = await supabase
        .from('photo_tags')
        .insert(
          tagIds.map(tagId => ({
            photo_id: photoId,
            tag_id: tagId,
          }))
        );

      if (error) {
        return { error: error.message };
      }
    }

    return {};
  } catch (error) {
    return { 
      error: error instanceof Error ? error.message : 'Unknown error' 
    };
  }
}

/**
 * Soft delete a photo (move to trash)
 */
export async function moveToTrash(
  photoId: string,
  organizationId: string
): Promise<{ success: boolean; error?: string }> {
  try {
    const { error: dbError } = await supabase
      .from('photos')
      .update({ deleted_at: new Date().toISOString() })
      .eq('id', photoId)
      .eq('organization_id', organizationId);

    if (dbError) {
      return { success: false, error: dbError.message };
    }

    return { success: true };
  } catch (error) {
    return { 
      success: false, 
      error: error instanceof Error ? error.message : 'Unknown error' 
    };
  }
}

/**
 * Restore a photo from trash
 */
export async function restoreFromTrash(
  photoId: string,
  organizationId: string
): Promise<{ success: boolean; error?: string }> {
  try {
    const { error: dbError } = await supabase
      .from('photos')
      .update({ deleted_at: null })
      .eq('id', photoId)
      .eq('organization_id', organizationId);

    if (dbError) {
      return { success: false, error: dbError.message };
    }

    return { success: true };
  } catch (error) {
    return { 
      success: false, 
      error: error instanceof Error ? error.message : 'Unknown error' 
    };
  }
}

/**
 * Permanently delete a photo from storage and database
 */
export async function deletePhoto(
  photoId: string,
  organizationId: string
): Promise<{ success: boolean; error?: string }> {
  try {
    // Get photo details
    const { data: photo, error: fetchError } = await supabase
      .from('photos')
      .select('filename')
      .eq('id', photoId)
      .eq('organization_id', organizationId)
      .single();

    if (fetchError || !photo) {
      return { success: false, error: 'Photo not found' };
    }

    const fileExtension = photo.filename.split('.').pop() || 'jpg';
    const storagePath = `${organizationId}/${photoId}.${fileExtension}`;

    // Delete from storage
    const { error: storageError } = await supabase.storage
      .from('photos')
      .remove([storagePath]);

    if (storageError) {
      console.error('Storage delete error:', storageError);
    }

    // Delete from database (cascades to photo_tags)
    const { error: dbError } = await supabase
      .from('photos')
      .delete()
      .eq('id', photoId)
      .eq('organization_id', organizationId);

    if (dbError) {
      return { success: false, error: dbError.message };
    }

    return { success: true };
  } catch (error) {
    return { 
      success: false, 
      error: error instanceof Error ? error.message : 'Unknown error' 
    };
  }
}

/**
 * Empty trash - permanently delete all trashed photos
 */
export async function emptyTrash(
  organizationId: string
): Promise<{ success: boolean; deletedCount?: number; error?: string }> {
  try {
    // Get all trashed photos
    const { data: trashedPhotos, error: fetchError } = await supabase
      .from('photos')
      .select('id, filename')
      .eq('organization_id', organizationId)
      .not('deleted_at', 'is', null);

    if (fetchError) {
      return { success: false, error: fetchError.message };
    }

    if (!trashedPhotos || trashedPhotos.length === 0) {
      return { success: true, deletedCount: 0 };
    }

    // Delete from storage
    const storagePaths = trashedPhotos.map(photo => {
      const fileExtension = photo.filename.split('.').pop() || 'jpg';
      return `${organizationId}/${photo.id}.${fileExtension}`;
    });

    const { error: storageError } = await supabase.storage
      .from('photos')
      .remove(storagePaths);

    if (storageError) {
      console.error('Storage delete error:', storageError);
    }

    // Delete from database
    const photoIds = trashedPhotos.map(p => p.id);
    const { error: dbError } = await supabase
      .from('photos')
      .delete()
      .in('id', photoIds);

    if (dbError) {
      return { success: false, error: dbError.message };
    }

    return { success: true, deletedCount: trashedPhotos.length };
  } catch (error) {
    return { 
      success: false, 
      error: error instanceof Error ? error.message : 'Unknown error' 
    };
  }
}

/**
 * Empty all photos - permanently delete ALL photos (not just trash)
 */
export async function emptyAllPhotos(
  organizationId: string
): Promise<{ success: boolean; deletedCount?: number; error?: string }> {
  try {
    // Get ALL photos for this organization
    const { data: allPhotos, error: fetchError } = await supabase
      .from('photos')
      .select('id, filename')
      .eq('organization_id', organizationId);

    if (fetchError) {
      return { success: false, error: fetchError.message };
    }

    if (!allPhotos || allPhotos.length === 0) {
      return { success: true, deletedCount: 0 };
    }

    // Delete from storage
    const storagePaths = allPhotos.map(photo => {
      const fileExtension = photo.filename.split('.').pop() || 'jpg';
      return `${organizationId}/${photo.id}.${fileExtension}`;
    });

    const { error: storageError } = await supabase.storage
      .from('photos')
      .remove(storagePaths);

    if (storageError) {
      console.error('Storage delete error:', storageError);
    }

    // Delete from database (cascades to photo_tags, photo_folders, photo_colors)
    const photoIds = allPhotos.map(p => p.id);
    const { error: dbError } = await supabase
      .from('photos')
      .delete()
      .in('id', photoIds);

    if (dbError) {
      return { success: false, error: dbError.message };
    }

    return { success: true, deletedCount: allPhotos.length };
  } catch (error) {
    return { 
      success: false, 
      error: error instanceof Error ? error.message : 'Unknown error' 
    };
  }
}
